import { useDataContext } from "@/context/DataContext";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { usePublicDoctors } from "@/hooks/useCMSDoctors";
import AddToCalendar from "@/pages/booking/components/AddToCalendar";
import BookingRefCard from "@/pages/booking/components/BookingRefCard";
import { getLocalized, getLocalizedArray, isRTL } from "@/utils/localization";

export interface BookingConfirmationState {
  bookingRef: string;
  serviceId?: string;
  doctorId: string;
  packageId?: string;
  date: string;
  time: string;
  visitType: string;
  fullName: string;
  phone: string;
  email?: string;
}

const visitTypeKeys: Record<string, string> = {
  first: "booking.step2.first_visit",
  followup: "booking.step2.followup",
  consultation: "booking.step2.consultation",
};

const accentMap: Record<string, { bg: string; text: string; border: string; iconBg: string }> = {
  teal:   { bg: "bg-brand-cream-100", text: "text-brand-forest-700", border: "border-brand-cream-300", iconBg: "bg-brand-cream-200" },
  rose:   { bg: "bg-rose-50",   text: "text-rose-700",   border: "border-rose-200",   iconBg: "bg-rose-100" },
  orange: { bg: "bg-orange-50", text: "text-orange-700", border: "border-orange-200", iconBg: "bg-orange-100" },
  pink:   { bg: "bg-pink-50",   text: "text-pink-700",   border: "border-pink-200",   iconBg: "bg-pink-100" },
  green:  { bg: "bg-green-50",  text: "text-green-700",  border: "border-green-200",  iconBg: "bg-green-100" },
  violet: { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200", iconBg: "bg-violet-100" },
  cyan:   { bg: "bg-cyan-50",   text: "text-cyan-700",   border: "border-cyan-200",   iconBg: "bg-cyan-100" },
  amber:  { bg: "bg-amber-50",  text: "text-amber-700",  border: "border-amber-200",  iconBg: "bg-amber-100" },
};

export default function BookingConfirmationPage() {
  const { t, i18n } = useTranslation();
  const { services: servicesData, packages: allPackages, posts: blogPosts } = useDataContext();

  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as BookingConfirmationState | null;
  const [confettiDone, setConfettiDone] = useState(false);
  const { doctors: doctorsDetailed } = usePublicDoctors();

  const formatDateDisplay = (dateStr: string) => {
    if (!dateStr) return "";
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const monthNamesAr = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];
      const monthNamesEn = ["January","February","March","April","May","June","July","August","September","October","November","December"];
      const monthNames = i18n.language?.startsWith("en") ? monthNamesEn : monthNamesAr;
      const monthIndex = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      return `${day} ${monthNames[monthIndex]} ${year}`;
    }
    return dateStr;
  };

  useEffect(() => {
    if (!state) {
      navigate("/booking", { replace: true });
      return;
    }
    const t = setTimeout(() => setConfettiDone(true), 2000);
    return () => clearTimeout(t);
  }, [state, navigate]);

  if (!state) return null;

  const currentLang = i18n.language;
  const rtl = isRTL(currentLang);

  const doctorIds = state.doctorId ? state.doctorId.split(",") : [];
  const selectedDoctors = doctorsDetailed.filter((d) => doctorIds.includes(d.id) || doctorIds.includes(d.doctor_id));
  const doctorNames = selectedDoctors.map(d => getLocalized(currentLang, d.name, (d as any).nameEn || d.name_en)).join(rtl ? " ، " : ", ");
  const totalDoctorFees = selectedDoctors.reduce((acc, d) => acc + (parseInt(d.consultationFee || "0") || 0), 0);

  const selectedServiceIds = state.serviceId ? state.serviceId.split(",") : [];
  const selectedServices = servicesData.filter((s) => selectedServiceIds.includes(s.id) || selectedServiceIds.includes(s.service_id));
  const getServicePrice = (s: any): number => {
    if (s.price && !isNaN(Number(s.price)) && Number(s.price) > 0) return Number(s.price);
    if (s.prices && s.prices.length > 0) {
      const p0 = Number(s.prices[0].price);
      if (!isNaN(p0) && p0 > 0) return p0;
    }
    return 0;
  };
  const totalServicePrice = selectedServices.reduce((acc, s) => acc + getServicePrice(s), 0);
  const totalFees = totalServicePrice + totalDoctorFees;

  const pkg = state.packageId ? allPackages.find((p) => p.id === state.packageId) : null;
  const accent = pkg ? (accentMap[pkg.accentColor] ?? accentMap.teal) : accentMap.teal;

  const calendarDescription = [
    t("booking.confirm.calendar_event"),
    `${t("booking.confirm.doctors")}: ${doctorNames}`,
    `${t("booking.confirm.visit_type")}: ${t(visitTypeKeys[state.visitType] ?? state.visitType)}`,
    pkg ? `${t("booking.confirm.package")}: ${getLocalized(currentLang, pkg.name, (pkg as any).nameEn || pkg.name_en)}` : "",
    `${t("booking.confirmation.ref")}: ${state.bookingRef}`,
    t("booking.confirm.support_phone"),
  ].filter(Boolean).join("\n");

  return (
    <div className="min-h-screen bg-brand-cream-50 font-sans">
      <Navbar />

      {/* ── Hero ── */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-brand-forest-700 to-brand-forest-900 relative overflow-hidden">
        {/* Background texture */}
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20medical%20pattern%20healthcare%20symbols%20minimal%20white%20lines%20on%20dark%20forest%20green%20background%20geometric%20pattern%20clean%20modern%20precision%20medicine&width=1440&height=320&seq=confirm-hero-v3&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Animated rings */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className={`w-64 h-64 rounded-full border border-white/10 transition-all duration-1000 ${confettiDone ? "scale-150 opacity-0" : "scale-100 opacity-100"}`}></div>
          <div className={`absolute w-48 h-48 rounded-full border border-white/15 transition-all duration-700 delay-200 ${confettiDone ? "scale-125 opacity-0" : "scale-100 opacity-100"}`}></div>
        </div>

        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8 text-center">
          {/* Success icon */}
          <div className="relative inline-flex items-center justify-center mb-5">
            <div className="w-20 h-20 flex items-center justify-center bg-white/15 backdrop-blur-sm rounded-full border-2 border-white/30">
              <i className="ri-checkbox-circle-fill text-white text-4xl"></i>
            </div>
            <div className="absolute -top-1 -right-1 w-6 h-6 flex items-center justify-center bg-green-400 rounded-full border-2 border-white">
              <i className="ri-check-line text-white text-xs"></i>
            </div>
          </div>

          <h1 className="text-3xl md:text-4xl font-black text-white mb-3">
            {t("booking.confirmation.heading")}
          </h1>
          <p className="text-brand-cream-200 text-base mb-6">
            {t("booking.confirm.hello", { name: state.fullName })}
          </p>

          {/* Quick summary pills */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            <span className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20">
              <i className="ri-calendar-line text-brand-cream-300"></i>
              {formatDateDisplay(state.date)}
            </span>
            <span className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20">
              <i className="ri-time-line text-brand-cream-300"></i>
              {state.time}
            </span>
            {selectedDoctors.length > 0 && (
              <span className="flex items-center gap-1.5 bg-white/15 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20">
                <i className="ri-user-heart-line text-brand-cream-300"></i>
                {t("booking.confirm.doctors")}: {doctorNames}
              </span>
            )}
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 space-y-5">

        {/* ── Booking Reference Card ── */}
        <BookingRefCard
          bookingRef={state.bookingRef}
          phone={state.phone}
          patientName={state.fullName}
        />

        {/* ── Appointment Details + Add to Calendar ── */}
        <div className="bg-white rounded-2xl border border-brand-cream-200 p-6">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <h2 className="text-base font-black text-gray-900 flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center bg-brand-cream-100 rounded-lg">
                <i className="ri-calendar-check-line text-brand-forest-600 text-sm"></i>
              </div>
              {t("booking.confirm.details_title")}
            </h2>
            <AddToCalendar
              title={`Medical appointment — ${doctorNames || "The Medical Avenue"}`}
              date={formatDateDisplay(state.date)}
              time={state.time}
              location={t("booking.confirm.location_val")}
              description={calendarDescription}
            />
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {[
              { icon: "ri-calendar-line",    label: t("booking.confirm.date"),      value: formatDateDisplay(state.date),                 color: "bg-brand-cream-100 text-brand-forest-600" },
              { icon: "ri-time-line",        label: t("booking.confirm.time"),      value: state.time,                                    color: "bg-brand-cream-100 text-brand-forest-600" },
              { icon: "ri-stethoscope-line", label: t("booking.confirm.visit_type"), value: t(visitTypeKeys[state.visitType] ?? state.visitType), color: "bg-amber-100 text-amber-600" },
              { icon: "ri-map-pin-line",     label: t("booking.confirm.location"),   value: t("booking.confirm.main_clinic"),              color: "bg-rose-100 text-rose-600" },
            ].map((item, i) => (
              <div key={i} className="bg-brand-cream-50 rounded-xl p-3 text-center">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg mx-auto mb-2 ${item.color}`}>
                  <i className={`${item.icon} text-sm`}></i>
                </div>
                <p className="text-[10px] text-gray-400 mb-0.5">{item.label}</p>
                <p className="text-xs font-bold text-gray-800 leading-tight">{item.value}</p>
              </div>
            ))}
          </div>

          {/* Services card list */}
          {(() => {
            if (selectedServices.length === 0) return null;
            return (
              <div className="mb-4 p-4 rounded-xl bg-brand-cream-50 border border-brand-cream-200">
                <p className="text-xs text-gray-400 mb-2 font-semibold">{t("booking.confirm.requested_services")}</p>
                <div className="flex flex-wrap gap-2">
                  {selectedServices.map((svc) => {
                    const sp = getServicePrice(svc);
                    const svcName = getLocalized(currentLang, svc.name, (svc as any).nameEn || svc.name_en);
                    return (
                      <span key={svc.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-cream-100 text-brand-forest-700 border border-brand-cream-300 text-xs font-semibold">
                        <i className={svc.icon}></i>
                        <span>{svcName.split("—")[0].trim()}</span>
                        {sp > 0 && <span className="font-bold text-brand-forest-800 mr-1">({sp.toLocaleString()} {t("common.sar") || "ريال"})</span>}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* Doctors cards */}
          {selectedDoctors.length > 0 && (
            <div className="space-y-3 mb-4">
              <p className="text-xs text-gray-400 font-semibold mb-1">{t("booking.confirm.selected_doctors")}</p>
              {selectedDoctors.map((doc) => {
                const docName = getLocalized(currentLang, doc.name, (doc as any).nameEn || doc.name_en);
                const docSpecialty = getLocalized(currentLang, doc.specialty, (doc as any).specialtyEn || doc.specialty_en);
                const docExperience = getLocalized(currentLang, doc.experience, (doc as any).experienceEn || doc.experience_en);

                return (
                  <div key={doc.id} className="flex items-center gap-4 bg-brand-cream-50 rounded-xl p-4 border border-brand-cream-200">
                    <img
                      src={doc.image}
                      alt={docName}
                      className="w-14 h-14 rounded-xl object-cover object-top flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-black text-gray-900 text-sm">{docName}</p>
                      <p className="text-brand-forest-600 text-xs font-semibold">{docSpecialty}</p>
                      <p className="text-gray-400 text-xs mt-0.5">{docExperience}</p>
                    </div>
                    {doc.consultationFee ? (
                      <div className="text-left flex-shrink-0">
                        <p className="text-xs text-gray-400">{t("booking.confirm.consultation_fees")}</p>
                        <p className="text-sm font-black text-brand-forest-700">{doc.consultationFee} {t("common.sar") || "ريال"}</p>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}

          {/* Fees breakdown */}
          {totalFees > 0 && (
            <div className="space-y-2 mt-4 pt-4 border-t border-brand-cream-200">
              {totalServicePrice > 0 && (
                <div className="flex items-center justify-between p-3 bg-brand-cream-100/60 rounded-xl border border-brand-cream-200">
                  <span className="text-xs font-bold text-gray-700">{t("booking.confirm.service_price") || "سعر الخدمة"}</span>
                  <span className="text-sm font-black text-brand-forest-800">{totalServicePrice.toLocaleString()} {t("common.sar") || "ريال"}</span>
                </div>
              )}
              {totalDoctorFees > 0 && totalServicePrice > 0 && (
                <div className="flex items-center justify-between p-3 bg-brand-cream-100/60 rounded-xl border border-brand-cream-200">
                  <span className="text-xs font-bold text-gray-700">{t("booking.confirm.consultation_fees")}</span>
                  <span className="text-sm font-black text-brand-forest-800">{totalDoctorFees.toLocaleString()} {t("common.sar") || "ريال"}</span>
                </div>
              )}
              {totalDoctorFees > 0 && totalServicePrice === 0 && (
                <div className="flex items-center justify-between p-3 bg-brand-cream-100/60 rounded-xl border border-brand-cream-200">
                  <span className="text-xs font-bold text-gray-700">{t("booking.confirm.consultation_fees")}</span>
                  <span className="text-sm font-black text-brand-forest-800">{totalDoctorFees.toLocaleString()} {t("common.sar") || "ريال"}</span>
                </div>
              )}
              {totalServicePrice > 0 && totalDoctorFees > 0 && (
                <div className="flex items-center justify-between p-3.5 bg-brand-forest-800 text-white rounded-xl shadow-sm">
                  <span className="text-xs font-bold">{t("booking.confirm.total_fees")}</span>
                  <span className="text-sm font-black">{totalFees.toLocaleString()} {t("common.sar") || "ريال"}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Package Summary ── */}
        {pkg && (
          <div className={`bg-white rounded-2xl border ${accent.border} p-6`}>
            <h2 className="text-base font-black text-gray-900 mb-5 flex items-center gap-2">
              <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${accent.iconBg}`}>
                <i className={`ri-gift-line ${accent.text} text-sm`}></i>
              </div>
              {t("booking.confirm.selected_package")}
            </h2>
            <div className={`rounded-xl p-4 ${accent.bg} border ${accent.border} mb-4`}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${accent.iconBg}`}>
                  <i className={`${pkg.icon} text-lg ${accent.text}`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <h3 className="font-black text-gray-900 text-sm">
                        {getLocalized(currentLang, pkg.name, (pkg as any).nameEn || pkg.name_en)}
                      </h3>
                      <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full mt-1 bg-white ${accent.text}`}>
                        {getLocalized(currentLang, pkg.category, (pkg as any).categoryEn || pkg.category_en)}
                      </span>
                    </div>
                    {pkg.price ? (
                      <div className="text-end flex-shrink-0">
                        {pkg.originalPrice && (
                          <p className="text-xs text-gray-400 line-through">{pkg.originalPrice.toLocaleString()} {t("common.sar") || "ريال"}</p>
                        )}
                        <p className={`text-xl font-black ${accent.text}`}>
                          {pkg.price.toLocaleString()} <span className="text-xs font-normal text-gray-500">{t("common.sar") || "ريال"}</span>
                        </p>
                        {pkg.originalPrice && (
                          <p className={`text-xs font-bold ${accent.text}`}>
                            {t("booking.confirm.saved")} {(pkg.originalPrice - pkg.price).toLocaleString()} {t("common.sar") || "ريال"}
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {getLocalizedArray(currentLang, pkg.features, pkg.features_en).map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                  <div className={`w-4 h-4 flex items-center justify-center rounded-full flex-shrink-0 ${accent.iconBg}`}>
                    <i className={`ri-check-line text-[9px] ${accent.text}`}></i>
                  </div>
                  {f}
                </div>
              ))}
            </div>
            {pkg.duration && (
              <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-500">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line text-gray-400"></i>
                </div>
                {t("booking.confirm.expected_duration")} <strong className="text-gray-700 mr-1">{getLocalized(currentLang, pkg.duration, pkg.duration_en)}</strong>
              </div>
            )}
          </div>
        )}

        {/* ── Patient Info ── */}
        <div className="bg-white rounded-2xl border border-brand-cream-200 p-6">
          <h2 className="text-base font-black text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-brand-cream-100 rounded-lg">
              <i className="ri-user-line text-brand-forest-600 text-sm"></i>
            </div>
            {t("booking.confirm.patient_info")}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-3 bg-brand-cream-50 rounded-xl p-3">
              <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg flex-shrink-0">
                <i className="ri-user-line text-gray-500 text-sm"></i>
              </div>
              <div>
                <p className="text-xs text-gray-400">{t("booking.confirm.name_label")}</p>
                <p className="text-sm font-bold text-gray-800">{state.fullName}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-brand-cream-50 rounded-xl p-3">
              <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg flex-shrink-0">
                <i className="ri-phone-line text-gray-500 text-sm"></i>
              </div>
              <div>
                <p className="text-xs text-gray-400">{t("booking.confirm.phone_label")}</p>
                <p className="text-sm font-bold text-gray-800">{state.phone}</p>
              </div>
            </div>
            {state.email && (
              <div className="flex items-center gap-3 bg-brand-cream-50 rounded-xl p-3 sm:col-span-2">
                <div className="w-7 h-7 flex items-center justify-center bg-white rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-gray-500 text-sm"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-400">{t("booking.confirm.email_label")}</p>
                  <p className="text-sm font-bold text-gray-800">{state.email}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Preparation Tips ── */}
        {pkg?.preparation && pkg.preparation.length > 0 && (
          <div className="bg-amber-50 rounded-2xl border border-amber-100 p-6">
            <h2 className="text-base font-black text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-lg">
                <i className="ri-lightbulb-line text-amber-600 text-sm"></i>
              </div>
              {t("booking.confirm.prep_instructions")}
            </h2>
            <div className="space-y-2.5">
              {pkg.preparation.map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-black flex-shrink-0 mt-0.5">
                    {i + 1}
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed">{step}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── What's Next ── */}
        <div className="bg-white rounded-2xl border border-brand-cream-200 p-6">
          <h2 className="text-base font-black text-gray-900 mb-5 flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-brand-cream-100 rounded-lg">
              <i className="ri-list-ordered text-brand-forest-600 text-sm"></i>
            </div>
            {t("booking.confirm.whats_next")}
          </h2>
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute right-4 top-4 bottom-4 w-px bg-brand-cream-200 hidden sm:block"></div>
            <div className="space-y-4">
              {[
                { icon: "ri-phone-line",    color: "bg-brand-cream-100 text-brand-forest-600", title: t("booking.confirm.timeline.1.title"),    desc: t("booking.confirm.timeline.1.desc") },
                { icon: "ri-message-2-line",color: "bg-amber-100 text-amber-600",              title: t("booking.confirm.timeline.2.title"),    desc: t("booking.confirm.timeline.2.desc") },
                { icon: "ri-map-pin-line",  color: "bg-rose-100 text-rose-600",                title: t("booking.confirm.timeline.3.title"), desc: t("booking.confirm.timeline.3.desc") },
                { icon: "ri-file-text-line",color: "bg-violet-100 text-violet-600",            title: t("booking.confirm.timeline.4.title"), desc: t("booking.confirm.timeline.4.desc") },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 sm:pr-2">
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl flex-shrink-0 relative z-10 ${item.color}`}>
                    <i className={`${item.icon} text-sm`}></i>
                  </div>
                  <div className="flex-1 pt-1">
                    <p className="text-sm font-bold text-gray-800">{item.title}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                  </div>
                  <span className="text-xs font-black text-gray-200 pt-1 flex-shrink-0">{i + 1}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Location ── */}
        <div className="bg-white rounded-2xl border border-brand-cream-200 p-6">
          <h2 className="text-base font-black text-gray-900 mb-4 flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-rose-100 rounded-lg">
              <i className="ri-map-pin-line text-rose-600 text-sm"></i>
            </div>
            {t("booking.confirm.location_title")}
          </h2>
          <div className="flex items-start gap-3 mb-4">
            <div className="flex-1">
              <p className="text-sm font-bold text-gray-800">{t("booking.confirm.clinic_name")}</p>
              <p className="text-xs text-gray-500 mt-1">{t("booking.confirm.clinic_address")}</p>
              <p className="text-xs text-gray-500">{t("booking.confirm.clinic_hours")}</p>
            </div>
            <a
              href="https://maps.google.com/?q=24.6877,46.6753"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs font-semibold text-brand-forest-600 border border-brand-cream-300 px-3 py-2 rounded-lg hover:bg-brand-cream-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-navigation-line text-sm"></i>
              {t("booking.confirm.directions")}
            </a>
          </div>
          <div className="rounded-xl overflow-hidden h-44 bg-brand-cream-100">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3624.6!2d46.6753!3d24.6877!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjTCsDQxJzE1LjciTiA0NsKwNDAnMzEuMSJF!5e0!3m2!1sar!2ssa!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="موقع العيادة"
            ></iframe>
          </div>
        </div>

        {/* ── Action Buttons ── */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Link
            to="/"
            className="flex-1 flex items-center justify-center gap-2 bg-brand-forest-600 hover:bg-brand-forest-700 text-white font-bold py-3.5 rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm"
          >
            <i className="ri-home-line"></i>
            {t("booking.confirmation.home")}
          </Link>
          <Link
            to="/booking"
            className="flex-1 flex items-center justify-center gap-2 border-2 border-brand-forest-600 text-brand-forest-600 font-bold py-3.5 rounded-full hover:bg-brand-cream-50 transition-colors whitespace-nowrap cursor-pointer text-sm"
          >
            <i className="ri-calendar-2-line"></i>
            {t("booking.confirmation.another")}
          </Link>
          <a
            href={`https://wa.me/966500000000?text=${encodeURIComponent(`مرحباً، رقم حجزي هو ${state.bookingRef}، أود الاستفسار عن موعدي`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 border-2 border-green-200 text-green-700 font-bold py-3.5 rounded-full hover:bg-green-50 transition-colors whitespace-nowrap cursor-pointer text-sm"
          >
            <i className="ri-whatsapp-line text-green-600"></i>
            {t("booking.confirm.contact_us")}
          </a>
        </div>

        {/* ── Cancel Note ── */}
        <div className="flex items-start gap-3 bg-brand-cream-50 rounded-xl p-4 border border-brand-cream-200">
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-brand-forest-400 text-sm"></i>
          </div>
          <p className="text-xs text-gray-500 leading-relaxed">
            {t("booking.confirm.cancel_note", { phone: "011 234 5678", ref: state.bookingRef })}
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
